import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {NatResidentBikeParkingUseChangeApplyLocationState} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatResidentBikeParkingUseChangeApplyForm from './resident/NatResidentBikeParkingUseChangeApplyForm'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {
    NatPutBikeParkingUseChangeApplyRequest,
    NatPutBikeParkingUseChangeApplyResponse,
} from '../repository/NatPutBikeParkingUseChangeApply'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentBikeParkingUseChangeApplyLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    useArea: Nullable<string>
    changeType: Nullable<string>
    changeStartDate: Nullable<string>
    changeEndDate: Nullable<string>
    userFamilyName: Nullable<string>
    userFirstName: Nullable<string>
    userAttribute: Nullable<string>
    ownerFamilyName: Nullable<string>
    ownerFirstName: Nullable<string>
    bikeType: Nullable<string>
    bikeName: Nullable<string>
    bikeRegistrationNumber: Nullable<string>
    engineSize: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentBikeParkingUseChangeApplyInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            useArea: null,
            changeType: null,
            changeStartDate: null,
            changeEndDate: null,
            userFamilyName: null,
            userFirstName: null,
            userAttribute: null,
            ownerFamilyName: null,
            ownerFirstName: null,
            bikeType: null,
            bikeName: null,
            bikeRegistrationNumber: null,
            engineSize: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, applicationRepo, eventEmitter} = this.props
        const {applyInfo, applyId} = this.props.location.state

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultUseArea = applyInfo?.useArea ?? null
        let defaultChangeType = applyInfo?.changeType ?? null
        let defaultChangeStartDate = applyInfo?.changeStartDate ?? null
        let defaultChangeEndDate = applyInfo?.changeEndDate ?? null
        let defaultUserFamilyName = applyInfo?.userFamilyName ?? null
        let defaultUserFirstName = applyInfo?.userFirstName ?? null
        let defaultUserAttribute = applyInfo?.userAttribute ?? null
        let defaultOwnerFamilyName = applyInfo?.ownerFamilyName ?? null
        let defaultOwnerFirstName = applyInfo?.ownerFirstName ?? null
        let defaultBikeType = applyInfo?.bikeType ?? null
        let defaultBikeName = applyInfo?.bikeName ?? null
        let defaultBikeRegistrationNumber = applyInfo?.bikeRegistrationNumber ?? null
        let defaultEngineSize = applyInfo?.engineSize ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const bikeParkingUseChangeApplyDetail = await this.props.asyncCallFunction(
                    applicationRepo.getBikeParkingUseChangeApplyForResident.bind(
                        applicationRepo, condominiumCd, applyId),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (bikeParkingUseChangeApplyDetail?.details) {
                    defaultUseArea = bikeParkingUseChangeApplyDetail.details.useArea
                    defaultChangeType = bikeParkingUseChangeApplyDetail.details.changeType
                    defaultChangeStartDate = bikeParkingUseChangeApplyDetail.details.changeStartDate
                    defaultChangeEndDate = bikeParkingUseChangeApplyDetail.details.changeEndDate
                    defaultUserFamilyName = bikeParkingUseChangeApplyDetail.details.userFamilyName
                    defaultUserFirstName = bikeParkingUseChangeApplyDetail.details.userFirstName
                    defaultUserAttribute = bikeParkingUseChangeApplyDetail.details.userAttribute
                    defaultOwnerFamilyName = bikeParkingUseChangeApplyDetail.details.ownerFamilyName
                    defaultOwnerFirstName = bikeParkingUseChangeApplyDetail.details.ownerFirstName
                    defaultBikeType = bikeParkingUseChangeApplyDetail.details.bikeType
                    defaultBikeName = bikeParkingUseChangeApplyDetail.details.bikeName
                    defaultBikeRegistrationNumber = bikeParkingUseChangeApplyDetail.details.bikeRegistrationNumber
                    defaultEngineSize = bikeParkingUseChangeApplyDetail.details.engineSize
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            useArea: defaultUseArea,
            changeType: defaultChangeType,
            changeStartDate: defaultChangeStartDate,
            changeEndDate: defaultChangeEndDate,
            userFamilyName: defaultUserFamilyName,
            userFirstName: defaultUserFirstName,
            userAttribute: defaultUserAttribute,
            ownerFamilyName: defaultOwnerFamilyName,
            ownerFirstName: defaultOwnerFirstName,
            bikeType: defaultBikeType,
            bikeName: defaultBikeName,
            bikeRegistrationNumber: defaultBikeRegistrationNumber,
            engineSize: defaultEngineSize,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutBikeParkingUseChangeApplyRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutBikeParkingUseChangeApplyResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyBikeParkingUseChangeApply.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentBikeParkingUseChangeApplyLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        useArea: request.useArea,
                        changeType: request.changeType,
                        changeStartDate: request.changeStartDate,
                        changeEndDate: request.changeEndDate,
                        userFamilyName: request.userFamilyName,
                        userFirstName: request.userFirstName,
                        userAttribute: request.userAttribute,
                        ownerFamilyName: request.ownerFamilyName,
                        ownerFirstName: request.ownerFirstName,
                        bikeType: request.bikeType,
                        bikeName: request.bikeName,
                        bikeRegistrationNumber: request.bikeRegistrationNumber,
                        engineSize: request.engineSize,
                    },
                    applyId: this.props.location.state.applyId,
                }

                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/bikeParkingUsesChange/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.location.state.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/bikeParkingUsesChange/${this.props.location.state.applyId}/copy`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications/bikeParkingUsesChange/agreement`
                    }>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    バイク変更届
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentBikeParkingUseChangeApplyForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentBikeParkingUseChangeApplyInputPage)
